/**
 * Global Client-Side Error Handler
 * Captures runtime errors and logs them in JSON format
 * Compatible with Docker logging and optional backend API submission
 */

interface ClientErrorLog {
  level: 'critical';
  message: string;
  stack?: string;
  file?: string;
  line?: number;
  column?: number;
  requestId: string;
  timestamp: string;
  service: 'client';
  environment: string;
  userAgent: string;
  url: string;
}

// Buffer for offline errors
const errorBuffer: ClientErrorLog[] = [];
const MAX_BUFFER_SIZE = 50;

/**
 * Save error buffer to localStorage
 */
function saveErrorBuffer(): void {
  try {
    localStorage.setItem('client_error_buffer', JSON.stringify(errorBuffer));
  } catch (error) {
    console.warn('[Client Error Handler] Failed to save error buffer:', error);
  }
}

/**
 * Load error buffer from localStorage
 */
function loadErrorBuffer(): void {
  try {
    const stored = localStorage.getItem('client_error_buffer');
    if (stored) {
      const parsed = JSON.parse(stored);
      errorBuffer.push(...parsed);
    }
  } catch (error) {
    console.warn('[Client Error Handler] Failed to load error buffer:', error);
  }
}

/**
 * Send buffered errors to backend
 */
async function flushErrorBuffer(): Promise<void> {
  if (errorBuffer.length === 0) return;

  const errorsToSend = [...errorBuffer];

  try {
    const token = localStorage.getItem('token');
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch('/api/logs/client/batch', {
      method: 'POST',
      body: JSON.stringify({ errors: errorsToSend }),
      headers
    });

    if (response.ok) {
      // Remove sent errors from buffer
      errorBuffer.splice(0, errorsToSend.length);
      saveErrorBuffer();
    }
  } catch (error) {
    console.warn('[Client Error Handler] Failed to flush error buffer:', error);
  }
}

/**
 * Report error to the troubleshoot agent for AI diagnosis
 */
function reportToTroubleshootAgent(log: ClientErrorLog): void {
  try {
    const token = localStorage.getItem('token');
    const csrfToken = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/)?.[1] ?? '';
    fetch('/api/ai/troubleshoot/report-ui-error', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token || ''}`, 'X-CSRF-Token': csrfToken },
      body: JSON.stringify({
        message: log.message,
        stack: log.stack,
        component: log.file,
        url: log.url,
        type: log.stack?.includes('Promise') ? 'unhandled_rejection' : 'runtime_error',
        userAction: `Line ${log.line || '?'}, Col ${log.column || '?'}`,
      }),
    }).catch(() => {});
  } catch { /* fail silently */ }
}

/**
 * Send error to backend API (optional)
 */
async function sendErrorToBackend(log: ClientErrorLog): Promise<void> {
  try {
    const response = await fetch('/api/logs/client', {
      method: 'POST',
      body: JSON.stringify(log),
      headers: { 'Content-Type': 'application/json' }
    });

    if (response.ok) {
      // Try to flush any buffered errors
      await flushErrorBuffer();
    }
  } catch (error) {
    // Buffer the error for later
    console.warn('[Client Error Handler] Failed to send error to backend, buffering locally:', error);
    errorBuffer.push(log);

    // Keep buffer size manageable
    if (errorBuffer.length > MAX_BUFFER_SIZE) {
      errorBuffer.shift(); // Remove oldest error
    }

    saveErrorBuffer();
  }
}

// Initialize buffer on load
loadErrorBuffer();

// Periodically try to flush buffer (every 30 seconds)
setInterval(flushErrorBuffer, 30000);

/**
 * Global error handler for uncaught errors
 */
window.onerror = (message, source, lineno, colno, error) => {
  const log: ClientErrorLog = {
    level: 'critical',
    message: message.toString(),
    stack: error?.stack || '',
    file: source || '',
    line: lineno || 0,
    column: colno || 0,
    requestId: crypto.randomUUID(),
    timestamp: new Date().toISOString(),
    service: 'client',
    environment: (import.meta as any).env?.MODE || 'development',
    userAgent: navigator.userAgent,
    url: window.location.href
  };

  // Log to console in JSON format (captured by Docker logs)
  console.error(JSON.stringify(log));

  // Always report to troubleshoot agent
  reportToTroubleshootAgent(log);

  // Also send to log endpoint if enabled
  if ((import.meta as any).env?.VITE_ENABLE_ERROR_REPORTING === 'true') {
    sendErrorToBackend(log);
  }
};

/**
 * Global handler for unhandled promise rejections
 */
window.onunhandledrejection = (event) => {
  const error = event.reason;
  const log: ClientErrorLog = {
    level: 'critical',
    message: error?.message || 'Unhandled Promise Rejection',
    stack: error?.stack,
    requestId: crypto.randomUUID(),
    timestamp: new Date().toISOString(),
    service: 'client',
    environment: (import.meta as any).env?.MODE || 'development',
    userAgent: navigator.userAgent,
    url: window.location.href
  };

  // Log to console in JSON format
  console.error(JSON.stringify(log));

  // Always report to troubleshoot agent
  reportToTroubleshootAgent(log);

  if ((import.meta as any).env?.VITE_ENABLE_ERROR_REPORTING === 'true') {
    sendErrorToBackend(log);
  }
};

/**
 * Manual error logging function for components
 */
export function logClientError(error: Error, context?: Record<string, any>): void {
  const log: ClientErrorLog = {
    level: 'critical',
    message: error.message,
    requestId: crypto.randomUUID(),
    timestamp: new Date().toISOString(),
    service: 'client',
    environment: (import.meta as any).env?.MODE || 'development',
    userAgent: navigator.userAgent,
    url: window.location.href,
    ...context
  };

  if (error.stack !== undefined) {
    log.stack = error.stack;
  }

  console.error(JSON.stringify(log));

  reportToTroubleshootAgent(log);

  if ((import.meta as any).env?.VITE_ENABLE_ERROR_REPORTING === 'true') {
    sendErrorToBackend(log);
  }
}

export default {
  logClientError
};